import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ChevronDown, XCircle } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnalysisPanel } from "../components/AnalysisPanel";
import { QuestionView } from "../components/QuestionView";
import { Badge, Button, Card, ErrorBanner, ProgressBar, Spinner, cx } from "../components/ui";
import { api } from "../lib/api";
import { formatDuration } from "../lib/format";

export function ExamResultPage() {
    const { id } = useParams<{ id: string }>();
    const [expanded, setExpanded] = useState<number | null>(null);
    const [onlyWrong, setOnlyWrong] = useState(true);

    const resultQuery = useQuery({
        queryKey: ["examResult", id],
        queryFn: () => api.exams.result(id!),
        enabled: Boolean(id)
    });
    const settingsQuery = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });

    if (resultQuery.isLoading) return <Spinner />;
    if (resultQuery.isError || !resultQuery.data) {
        return <ErrorBanner message="加载成绩失败" onRetry={() => void resultQuery.refetch()} />;
    }

    const result = resultQuery.data;
    const settings = settingsQuery.data?.settings;
    const wrongItems = result.items.filter((item) => !item.isCorrect);
    const visibleItems = onlyWrong ? wrongItems : result.items;

    return (
        <div className="space-y-6">
            <Card
                className={cx(
                    "space-y-4 p-6 text-center",
                    result.passed ? "border-good/40 bg-good-soft" : "border-bad/40 bg-bad-soft"
                )}
            >
                <div className="flex justify-center">
                    {result.passed ? (
                        <CheckCircle2 className="size-12 text-good" aria-hidden />
                    ) : (
                        <XCircle className="size-12 text-bad" aria-hidden />
                    )}
                </div>
                <div>
                    <h1 className="text-2xl font-semibold text-ink">{result.passed ? "通过了" : "还没通过"}</h1>
                    <p className="mt-1 text-sm text-ink-soft">
                        答对 {result.score} / {result.total} 题，及格线 {result.passingScore} 题 · 用时{" "}
                        {formatDuration(result.durationSeconds)}
                    </p>
                </div>
                <div className="mx-auto max-w-sm">
                    <ProgressBar
                        value={result.score}
                        max={result.total}
                        tone={result.passed ? "good" : "bad"}
                    />
                </div>
                <div className="flex flex-wrap justify-center gap-2 pt-1">
                    <Link to="/exam">
                        <Button variant="secondary">回到模拟考</Button>
                    </Link>
                    <Link to="/study?filter=DIFFICULT">
                        <Button>练习错题</Button>
                    </Link>
                </div>
            </Card>

            <section className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-sm font-semibold text-ink">逐题回顾</h2>
                    <div className="flex items-center gap-1 rounded-full border border-line bg-surface p-1">
                        <button
                            onClick={() => setOnlyWrong(true)}
                            className={cx(
                                "rounded-full px-3 py-1 text-sm",
                                onlyWrong ? "bg-brand text-white" : "text-ink-soft"
                            )}
                        >
                            只看错题 ({wrongItems.length})
                        </button>
                        <button
                            onClick={() => setOnlyWrong(false)}
                            className={cx(
                                "rounded-full px-3 py-1 text-sm",
                                !onlyWrong ? "bg-brand text-white" : "text-ink-soft"
                            )}
                        >
                            全部 ({result.items.length})
                        </button>
                    </div>
                </div>

                {visibleItems.length === 0 ? (
                    <Card className="p-6 text-center text-sm text-ink-soft">全部答对，没有错题。</Card>
                ) : null}

                <ul className="space-y-2">
                    {visibleItems.map((item) => {
                        const isOpen = expanded === item.position;
                        return (
                            <li key={item.position}>
                                <Card className="overflow-hidden">
                                    <button
                                        onClick={() => setExpanded(isOpen ? null : item.position)}
                                        aria-expanded={isOpen}
                                        className="flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-sunken"
                                    >
                                        {item.isCorrect ? (
                                            <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-good" aria-label="答对" />
                                        ) : (
                                            <XCircle className="mt-0.5 size-5 shrink-0 text-bad" aria-label="答错" />
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <p lang="de" className="line-clamp-2 text-sm text-ink">
                                                {item.question.text}
                                            </p>
                                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                                <Badge>第 {item.question.number} 题</Badge>
                                                {item.selectedOptionId === null ? (
                                                    <Badge tone="warn">未作答</Badge>
                                                ) : null}
                                            </div>
                                        </div>
                                        <ChevronDown
                                            className={cx(
                                                "mt-0.5 size-5 shrink-0 text-ink-soft transition-transform",
                                                isOpen && "rotate-180"
                                            )}
                                            aria-hidden
                                        />
                                    </button>

                                    {isOpen ? (
                                        <div className="space-y-6 border-t border-line px-4 py-5 sm:px-6">
                                            <QuestionView
                                                question={item.question}
                                                selectedOptionId={item.selectedOptionId}
                                                revealed
                                                showTranslation={settings?.showQuestionTranslation ?? false}
                                            />
                                            <div className="border-t border-line pt-5">
                                                <AnalysisPanel
                                                    analysis={item.question.analysis}
                                                    showEnglish={settings?.showEnglish ?? false}
                                                    showChinese={settings?.showChinese ?? true}
                                                />
                                            </div>
                                        </div>
                                    ) : null}
                                </Card>
                            </li>
                        );
                    })}
                </ul>
            </section>
        </div>
    );
}
